import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ChevronLeft, MapPin, Phone, Printer, Mail, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import SiteHeader from '../components/SiteHeader';
import SiteBackground from '../components/SiteBackground';
import api from '../utils/api';

const OfficeDetailPage = () => {
  const { officeId } = useParams();
  const navigate = useNavigate();
  const [office, setOffice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    const fetchOffice = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.get(`/offices/${officeId}`);
        setOffice(response.data);
      } catch (err) {
        console.error('事業所の取得に失敗しました:', err);
        setError('事業所が見つかりませんでした');
      } finally {
        setLoading(false);
      }
    };
    fetchOffice();
  }, [officeId]);

  const InfoRow = ({ icon: Icon, label, value }) => (
    <div className="flex items-start gap-4 py-4 border-b border-amber-500/10 last:border-b-0">
      <div className="flex items-center gap-2 w-28 flex-shrink-0 text-amber-500">
        <Icon className="w-4 h-4" />
        <span className="text-sm">{label}</span>
      </div>
      <span className="text-gray-700 whitespace-pre-line">{value}</span>
    </div>
  );

  return (
    <div className="min-h-screen text-gray-900 relative">
      <SiteBackground />
      <SiteHeader />

      {/* 戻るボタン */}
      <div className="fixed top-3 right-4 sm:top-5 sm:right-6 z-[60]">
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate(-1)}
          className="border-amber-500/30 text-amber-500 hover:bg-amber-500/10"
          data-testid="office-back-btn"
        >
          <ChevronLeft className="w-4 h-4 mr-1" />
          戻る
        </Button>
      </div>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-40 pb-20 relative">
        {loading ? (
          <div className="text-center text-gray-600 py-20" data-testid="office-loading">読み込み中...</div>
        ) : error ? (
          <div className="text-center py-20">
            <p className="text-red-400 mb-6" data-testid="office-error">{error}</p>
            <Link to="/" className="inline-flex items-center text-amber-500 hover:text-amber-500">
              <ChevronLeft className="w-4 h-4 mr-1" />
              トップに戻る
            </Link>
          </div>
        ) : office && (
          <article data-testid="office-detail-article">
            {office.image_url && (
              <div className="rounded-2xl overflow-hidden mb-8 border border-amber-500/20 bg-white">
                <img
                  src={office.image_url}
                  alt={office.name}
                  className="w-full h-auto object-contain"
                />
              </div>
            )}

            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-gray-900 via-amber-700 to-amber-500 bg-clip-text text-transparent leading-tight" data-testid="office-name">
              {office.name}
            </h1>

            {office.description && (
              <p className="text-lg text-gray-600 mb-10 leading-relaxed whitespace-pre-line" data-testid="office-description">
                {office.description}
              </p>
            )}

            <div className="rounded-2xl bg-gradient-to-br from-gray-50/80 to-gray-100/80 border border-amber-500/20 backdrop-blur-sm p-6 sm:p-8">
              {office.address && <InfoRow icon={MapPin} label="所在地" value={office.address} />}
              {office.phone && <InfoRow icon={Phone} label="電話番号" value={office.phone} />}
              {office.fax && <InfoRow icon={Printer} label="FAX" value={office.fax} />}
              {office.email && <InfoRow icon={Mail} label="メール" value={office.email} />}
              {office.business_hours && <InfoRow icon={Clock} label="営業時間" value={office.business_hours} />}
            </div>

            {/* この事業所への問い合わせ */}
            {(office.email || office.phone) && (
              <div className="mt-8 flex flex-col sm:flex-row gap-4" data-testid="office-contact-actions">
                {office.email && (
                  <a
                    href={`mailto:${office.email}?subject=${encodeURIComponent(office.name + 'へのお問い合わせ')}`}
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 text-gray-900 font-semibold hover:from-amber-500 hover:to-amber-600 transition-all shadow-lg shadow-amber-500/20"
                    data-testid="office-contact-email"
                  >
                    <Mail className="w-5 h-5" />
                    この事業所にメールで問い合わせる
                  </a>
                )}
                {office.phone && (
                  <a
                    href={`tel:${office.phone.replace(/[^0-9+]/g, '')}`}
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl border border-amber-500/40 text-amber-500 font-semibold hover:bg-amber-500/10 transition-all"
                    data-testid="office-contact-phone"
                  >
                    <Phone className="w-5 h-5" />
                    電話で問い合わせる
                  </a>
                )}
              </div>
            )}

            {/* アクセス（地図） */}
            {office.address && (
              <div className="mt-12" data-testid="office-map">
                <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
                  <MapPin className="w-6 h-6 text-amber-500" />
                  アクセス
                </h2>
                <div className="rounded-2xl overflow-hidden border border-amber-500/20">
                  <iframe
                    title={`${office.name}の地図`}
                    src={`https://maps.google.com/maps?q=${encodeURIComponent(office.address)}&z=15&output=embed`}
                    width="100%"
                    height="380"
                    style={{ border: 0, display: 'block' }}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    allowFullScreen
                  />
                </div>
              </div>
            )}

            <div className="mt-12 pt-8 border-t border-amber-500/20">
              <Link
                to="/"
                className="inline-flex items-center text-amber-500 hover:text-amber-500 transition-colors"
                data-testid="office-return-home"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                トップページに戻る
              </Link>
            </div>
          </article>
        )}
      </main>
    </div>
  );
};

export default OfficeDetailPage;
